/**
 * Core entities. Every synced entity carries the same bookkeeping:
 * a client-generated UUIDv7 id, creation time, the HLC of its last write
 * and a tombstone (deletedAt) instead of physical deletion.
 *
 * Money is integer minor units + ISO currency. Dates without time are
 * local calendar dates as "YYYY-MM-DD" strings (an expense on the 31st
 * stays on the 31st in every timezone).
 */
import { isValidCurrency } from './money';

export interface Synced {
  id: string;
  createdAt: number;
  updatedHlc: string;
  deletedAt: number | null;
}

export type IsoDate = string; // YYYY-MM-DD

export interface User extends Synced {
  displayName: string;
  email: string | null;
  kind: 'local' | 'account';
}

/** Personal (just me), household (Home: shared), other (a holiday, a flat abroad: shared). */
export type BudgetType = 'personal' | 'household' | 'other';
export const BUDGET_TYPES: readonly BudgetType[] = ['personal', 'household', 'other'];

export interface BudgetSpace extends Synced {
  name: string;
  type: BudgetType;
  currency: string;
  billingCountry: string | null;
  monthlyTargetMinor: number | null;
  ownerUserId: string;
}

export type MemberRole = 'owner' | 'member';

/** A person in a budget. userId is null for someone who does not use the app (a child, a flatmate). */
export interface BudgetMember extends Synced {
  spaceId: string;
  userId: string | null;
  displayName: string;
  role: MemberRole;
}

export type CategoryKind = 'spending' | 'obligation';
export type CategoryNature = 'fixed' | 'variable' | 'discretionary';

/**
 * Default categories keep a systemKey and a null name so they display in
 * the app language; renaming stores a name. colorSlot is the category's
 * line color for life (1-7, 0 = Other).
 */
export interface Category extends Synced {
  spaceId: string;
  systemKey: string | null;
  name: string | null;
  kind: CategoryKind;
  nature: CategoryNature | null;
  colorSlot: number;
  sortOrder: number;
  /** Archived: out of the pickers, still labels its history. Null when active. */
  archivedAt?: number | null;
}

export interface Subcategory extends Synced {
  spaceId: string;
  categoryId: string;
  systemKey: string | null;
  name: string | null;
  /** Suggested consumption unit, e.g. kWh for Electricity. */
  defaultUnit: string | null;
  sortOrder: number;
  archivedAt?: number | null;
}

export const TRANSACTION_TYPES = ['expense', 'debt_payment', 'credit_card_payment', 'transfer', 'refund', 'income'] as const;
export type TransactionType = (typeof TRANSACTION_TYPES)[number];

export type Ownership = 'shared' | 'personal';

export interface Transaction extends Synced {
  /** The budget space (the brief's budgetId). */
  spaceId: string;
  categoryId: string;
  subcategoryId: string | null;
  type: TransactionType;
  /** Always positive; the type says which way it moves spending. In the budget's currency. */
  amountMinor: number;
  /** Always the budget's currency, so every total adds like with like. */
  currency: string;
  /**
   * Paid in another currency: what was actually paid (minor units of
   * originalCurrency). amountMinor is its value in the budget's currency.
   * Both set, or both null.
   */
  originalAmountMinor: number | null;
  originalCurrency: string | null;
  date: IsoDate;
  addedBy: string; // member id
  paidBy: string; // member id
  ownership: Ownership;
  /** Set when ownership is personal: whose it is. */
  ownerMemberId: string | null;
  /**
   * Whether this entry adds to spending totals. Fixed by type except for a
   * credit card bill, where the user says whether the card's purchases were
   * already tracked one by one (then the bill must not count again).
   */
  countsAsSpending: boolean;
  notes: string | null;
  provider: string | null;
  dueDate: IsoDate | null;
  periodStart: IsoDate | null;
  periodEnd: IsoDate | null;
  recurringTemplateId: string | null;
}

export type RecurringKind = 'fixed' | 'variable';
export type Frequency = 'monthly' | 'quarterly' | 'yearly';

/** Rent, Netflix (fixed: same amount) or Electricity, Credit card (variable: amount changes). */
export interface RecurringTemplate extends Synced {
  spaceId: string;
  categoryId: string;
  subcategoryId: string | null;
  name: string;
  kind: RecurringKind;
  type: TransactionType;
  /** Expected amount for fixed templates; null for variable ones (last month's amount is shown instead). */
  amountMinor: number | null;
  currency: string;
  frequency: Frequency;
  dayOfMonth: number | null;
  nextDueDate: IsoDate | null;
  paidBy: string | null;
  ownership: Ownership;
  ownerMemberId: string | null;
  countsAsSpending: boolean;
  active: boolean;
}

/**
 * Optional state of a loan or card at a payment (brief §13): what is left,
 * the interest rate, installments to go. Every field is optional; the bank
 * is the entry's provider and the due date the entry's due date.
 */
export interface DebtDetails extends Synced {
  spaceId: string;
  transactionId: string;
  remainingBalanceMinor: number | null;
  /** Annual rate in basis points: 12.5 % = 1250. */
  interestRateBp: number | null;
  installmentsLeft: number | null;
}

/** Entry types that can carry debt details. */
export const DEBT_TYPES: readonly TransactionType[] = ['debt_payment', 'credit_card_payment'];

/** Optional usage attached to a bill: 260 kWh, 74 m³, 620 GB. */
export interface ConsumptionData extends Synced {
  spaceId: string;
  transactionId: string;
  quantity: number;
  unit: string;
  periodStart: IsoDate | null;
  periodEnd: IsoDate | null;
}

export type ActivityAction = 'created' | 'updated' | 'deleted' | 'restored' | 'marked_paid' | 'conflict';

/** "Alex added Electricity — $82". Append-only household history. */
export interface ActivityEntry extends Synced {
  spaceId: string;
  actorMemberId: string | null;
  action: ActivityAction;
  entity: 'transaction' | 'recurring_template' | 'category' | 'member' | 'space';
  entityId: string;
  summary: Record<string, unknown>;
  at: number;
}

/* ------------------------------------------------------------------ rules */

export class ValidationError extends Error {
  constructor(
    readonly field: string,
    message: string,
  ) {
    super(`${field}: ${message}`);
    this.name = 'ValidationError';
  }
}

const ISO_DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export function isIsoDate(v: string): boolean {
  if (!ISO_DATE.test(v)) return false;
  const [y, m, d] = v.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  return dt.getUTCMonth() === m! - 1 && dt.getUTCDate() === d!;
}

export function requireIsoDate(field: string, v: string | null | undefined, optional = false) {
  if (v == null) {
    if (optional) return;
    throw new ValidationError(field, 'required');
  }
  if (!isIsoDate(v)) throw new ValidationError(field, `not a calendar date (YYYY-MM-DD): ${v}`);
}

export function requireCurrency(field: string, v: string) {
  if (!isValidCurrency(v)) throw new ValidationError(field, `not an ISO 4217 currency: ${v}`);
}

export function requirePositiveMinor(field: string, v: number) {
  if (!Number.isSafeInteger(v) || v <= 0) throw new ValidationError(field, 'must be a positive whole number of minor units');
}

export function requireText(field: string, v: string | null | undefined, max = 200) {
  if (v == null || v.trim() === '') throw new ValidationError(field, 'required');
  if (v.length > max) throw new ValidationError(field, `longer than ${max} characters`);
}

/** The spending rule per type (brief §14–15). Only a credit card bill is a choice. */
export function spendingRule(type: TransactionType): boolean | 'choose' {
  switch (type) {
    case 'expense':
    case 'debt_payment':
    case 'refund': // counts, with a negative sign in totals
      return true;
    case 'credit_card_payment':
      return 'choose';
    case 'transfer':
    case 'income':
      return false;
  }
}

/** Sign an entry contributes to spending totals: refunds reduce spending. */
export function spendingSign(t: Pick<Transaction, 'type' | 'countsAsSpending'>): -1 | 0 | 1 {
  // The type decides wherever its rule is fixed (a row merged from two devices may carry a stale flag).
  const rule = spendingRule(t.type);
  if (!(rule === 'choose' ? t.countsAsSpending : rule)) return 0;
  return t.type === 'refund' ? -1 : 1;
}
